/**
 * Dependency Graph
 *
 * Builds and maintains a dependency graph that represents field relationships.
 * The graph is dynamic and considers the active schema branch based on form data.
 */

import { JSONSchema7 } from 'json-schema';
import {
  DependencyGraph,
  FieldPath,
  ParsedSchema,
  DependencyManagerConfig,
} from './types';
import { parseSchema, normalizeFieldPath } from './schemaParser';
import { DEPENDENCY_MANAGER_ID_SEPARATOR } from '../utils';

/**
 * Builds a dependency graph from a parsed schema
 *
 * @param parsedSchema - The parsed schema information
 * @returns Dependency graph
 */
export function buildDependencyGraph(
  parsedSchema: ParsedSchema,
): DependencyGraph {
  return { ...parsedSchema.dependencies };
}

/**
 * Finds all fields that depend on the given field
 *
 * @param fieldPath - The field to find dependents for
 * @param graph - The dependency graph
 * @returns Array of dependent field paths
 */
export function findDependents(
  fieldPath: FieldPath,
  graph: DependencyGraph,
): FieldPath[] {
  const dependents: FieldPath[] = [];

  // Direct dependencies
  if (graph[fieldPath]) {
    dependents.push(...graph[fieldPath]);
  }

  // Find fields that depend on this field (reverse lookup)
  Object.entries(graph).forEach(([source, targets]) => {
    if (targets.includes(fieldPath) && !dependents.includes(source)) {
      dependents.push(source);
    }
  });

  return Array.from(new Set(dependents));
}

/**
 * Finds all fields in the dependency chain starting from the given field
 * Uses depth-first search to find all transitive dependencies
 *
 * @param fieldPath - The starting field
 * @param graph - The dependency graph
 * @param visited - Set of already visited fields (to prevent cycles)
 * @returns Array of all dependent field paths
 */
export function findAllDependents(
  fieldPath: FieldPath,
  graph: DependencyGraph,
  visited: Set<FieldPath> = new Set(),
): FieldPath[] {
  if (visited.has(fieldPath)) {
    return [];
  }

  visited.add(fieldPath);
  const directDependents = findDependents(fieldPath, graph);
  const allDependents = [...directDependents];

  directDependents.forEach(dependent => {
    const transitiveDependents = findAllDependents(dependent, graph, visited);
    transitiveDependents.forEach(td => {
      if (!allDependents.includes(td)) {
        allDependents.push(td);
      }
    });
  });

  return allDependents;
}

/**
 * Finds all fields that depend on the given field, organized by dependency level
 * Uses breadth-first search to group dependents by their distance from the trigger field
 *
 * This is crucial for cascade updates:
 * - Level 0: Direct dependents of the trigger field
 * - Level 1: Fields that depend on level 0 fields
 * - Level 2: Fields that depend on level 1 fields
 * - etc.
 *
 * Example:
 *   domain → dataproduct → identifier
 *
 *   When domain changes:
 *   - Level 0: [dataproduct]
 *   - Level 1: [identifier]
 *
 * This allows processing updates level by level, ensuring each level sees
 * the updated formData from the previous level.
 *
 * @param fieldPath - The starting field
 * @param graph - The dependency graph
 * @returns Array of arrays, where each inner array contains dependents at that level
 */
export function findDependentsByLevel(
  fieldPath: FieldPath,
  graph: DependencyGraph,
): FieldPath[][] {
  const levels: FieldPath[][] = [];
  const visited = new Set<FieldPath>();
  let currentLevel = [fieldPath];
  visited.add(fieldPath);

  while (currentLevel.length > 0) {
    const nextLevel: FieldPath[] = [];

    for (const field of currentLevel) {
      // Find direct dependents (fields that this field points to in the graph)
      const directDependents = graph[field] || [];

      for (const dependent of directDependents) {
        if (!visited.has(dependent)) {
          visited.add(dependent);
          nextLevel.push(dependent);
        }
      }
    }

    if (nextLevel.length > 0) {
      levels.push(nextLevel);
    }

    currentLevel = nextLevel;
  }

  return levels;
}

/**
 * Checks if there are circular dependencies in the graph
 *
 * @param graph - The dependency graph to check
 * @returns True if circular dependencies exist
 */
export function hasCircularDependencies(graph: DependencyGraph): boolean {
  const visited = new Set<FieldPath>();
  const recursionStack = new Set<FieldPath>();

  function hasCycle(fieldPath: FieldPath): boolean {
    visited.add(fieldPath);
    recursionStack.add(fieldPath);

    const dependents = graph[fieldPath] || [];
    for (const dependent of dependents) {
      if (!visited.has(dependent)) {
        if (hasCycle(dependent)) {
          return true;
        }
      } else if (recursionStack.has(dependent)) {
        return true;
      }
    }

    recursionStack.delete(fieldPath);
    return false;
  }

  for (const fieldPath of Object.keys(graph)) {
    if (!visited.has(fieldPath)) {
      if (hasCycle(fieldPath)) {
        return true;
      }
    }
  }

  return false;
}

/**
 * Performs topological sort on dependency graph starting from a trigger field
 * Returns fields in execution order (each field appears after its dependencies)
 *
 * Example:
 *   domain → dataproduct → identifier
 *
 *   When domain changes:
 *   Result: ['dataproduct', 'identifier']
 *
 * This ensures each field is processed after all its dependencies have been processed.
 *
 * @param triggerField - The field that changed (starting point)
 * @param graph - The dependency graph
 * @returns Array of fields in topological order
 */
export function topologicalSort(
  triggerField: FieldPath,
  graph: DependencyGraph,
): FieldPath[] {
  const result: FieldPath[] = [];
  const visited = new Set<FieldPath>();
  const visiting = new Set<FieldPath>(); // For cycle detection

  /**
   * DFS-based topological sort
   * Processes dependencies before the field itself
   */
  function visit(field: FieldPath): void {
    if (visited.has(field)) {
      return; // Already processed
    }

    if (visiting.has(field)) {
      // Cycle detected - skip to avoid infinite loop
      return;
    }

    visiting.add(field);

    // Visit all dependent fields first (fields that depend on this field)
    // If the exact field is not in the graph, try the normalized (template) path
    // then fall back to the parent field.
    // Schema-derived dependencies use template paths ([] for array indices),
    // while the trigger field carries actual indices (0, 1, 2, …).
    let dependents = graph[field] || [];
    if (dependents.length === 0) {
      // Try template path (replace numeric indices with [])
      const normalizedField = normalizeFieldPath(
        field,
        DEPENDENCY_MANAGER_ID_SEPARATOR,
      );
      if (normalizedField !== field) {
        dependents = graph[normalizedField] || [];
      }
    }
    if (dependents.length === 0) {
      // Try parent field - split by separator and take parent path
      const parts = field.split(DEPENDENCY_MANAGER_ID_SEPARATOR);
      if (parts.length > 1) {
        const parentField = parts
          .slice(0, -1)
          .join(DEPENDENCY_MANAGER_ID_SEPARATOR);
        dependents = graph[parentField] || [];
      }
    }

    for (const dependent of dependents) {
      visit(dependent);
    }

    visiting.delete(field);
    visited.add(field);

    // Add to result after all dependents have been processed
    // This ensures reverse topological order (we'll reverse at the end)
    // IMPORTANT: Exclude the trigger field itself to avoid processing it as its own dependent
    if (field !== triggerField) {
      result.push(field);
    }
  }

  // Start from trigger field
  visit(triggerField);

  // Reverse to get correct topological order
  // (dependencies before dependents)
  const reversed = result.reverse();
  return reversed;
}

/**
 * Cache for dependency graphs to avoid recomputation
 */
class DependencyGraphCache {
  private cache = new Map<string, DependencyGraph>();

  /**
   * Gets a cached dependency graph or builds a new one
   *
   * @param schema - The JSON Schema
   * @param config - Dependency manager configuration
   * @returns Dependency graph
   */
  get(schema: JSONSchema7, config: DependencyManagerConfig): DependencyGraph {
    const key = this.getCacheKey(schema);

    if (this.cache.has(key)) {
      return this.cache.get(key)!;
    }

    const parsedSchema = parseSchema(schema, config);
    const graph = buildDependencyGraph(parsedSchema);

    this.cache.set(key, graph);
    return graph;
  }

  /**
   * Clears the cache
   */
  clear(): void {
    this.cache.clear();
  }

  /**
   * Generates a cache key for a schema
   *
   * @param schema - The JSON Schema
   * @returns Cache key
   */
  private getCacheKey(schema: JSONSchema7): string {
    // Simple hash based on schema structure
    // In production, you might want a more sophisticated hash
    return JSON.stringify(schema);
  }
}

/**
 * Singleton instance of the dependency graph cache
 */
export const dependencyGraphCache = new DependencyGraphCache();
